import { Autocomplete, AutocompleteChangeDetails, Button, Grid, TextField, Tooltip } from '@mui/material';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CustomAccordion from '../CustomAccordion/CustomAccordion';
import TagsFilter from '../TagFilter/TagsFilter';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import SavedSearch, { ComponentType } from '../SavedSearch/SavedSearch';
import Swal from 'sweetalert2';
import SavedSearchApi, { SaveSavedSearchParam } from '../SavedSearch/api/SavedSearchApi';
import SavedSearchModel from '../SavedSearch/models/SavedSearchModel';
import { addSavedSearch, savedSearchSelector } from '../SavedSearch/reducers/SavedSearchReducer';
import { QueryItem } from '../Tonnage/models/TonnageQueryModel';
import SurveyorApi, { SearchSurveyorParam } from './api/SurveyorApi';
import SurveyorModel from './models/SurveyorModel';
import { SurveyorSearchSelector, resetSurveyorSearch, setSurveyorNames, setSurveyorSearchKeyword, setSurveyorSearchResults, setPortNames, setCountries, setSearchResultTotalRecord, setSelectedSurveyors, setTriggerSurveyorSearch, setTriggerFocusCompanyName } from './reducers/SurveyorSearchReducer';
import { contactInputSelector } from '../Contact/reducers/ContactInputReducer';
import PortMultipleSelector from '../PortSelector/PortMultipleSelector';
import PortModel from '../PortSelector/models/PortModel';

const SurveyorSearch: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const SurveyorSearchData = useSelector(SurveyorSearchSelector);
    const savedSearchData = useSelector(savedSearchSelector);
    const contactInputViewData = useSelector(contactInputSelector);

    const portsToQueryItems = (ports: PortModel[]) => {
        return _.map(ports, (port) => {
            return {
                id: port.id.toString(),
                value: port.name,
                positive: port.positive
            } as QueryItem
        })
    }

    const queryItemsToPorts = (queryItems: QueryItem[]) => {
        return _.map(queryItems, (queryItem) => {
            return {
                id: parseInt(queryItem.id),
                name: queryItem.value,
                positive: queryItem.positive
            } as PortModel
        })
    };

    const saveSavedSearch = () => {
        Swal.fire({
            title: 'Provide Saved Search Name',
            input: 'text',
            inputAttributes: {
                autocapitalize: 'off'
            },
            showCancelButton: true,
            confirmButtonText: 'Sumbit',
            showLoaderOnConfirm: true,
            allowOutsideClick: false,
            confirmButtonColor: '#1976d2',
            preConfirm: (name) => {
                if (!name || name.length === 0) {
                    Swal.showValidationMessage("Name ")
                }
            },
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(showLoading(true));

                SavedSearchApi.Save({
                    name: result.value ? result.value : "",
                    component: ComponentType.Surveyor,
                    queryJson: JSON.stringify(SurveyorSearchData.query)
                } as SaveSavedSearchParam)
                    .then((response: BaseResponse<SavedSearchModel>) => {
                        if (response.success) {
                            dispatch(addSavedSearch(response.data));
                            Utility.Alert.Success("Save Successful");
                        } else {
                            Utility.Alert.Error(response.message);
                        }
                    })
                    .catch((error) => {
                        Utility.Alert.Error(error);
                    })
                    .finally(() => {
                        dispatch(showLoading(false));
                    });
            }
        })
    }

    useEffect(() => {
        if (SurveyorSearchData.triggerSearch === 0) {
            return;
        }

        dispatch(showLoading(true));

        SurveyorApi.Search({
            query: SurveyorSearchData.query
        } as SearchSurveyorParam)
            .then((response: BaseResponse<SurveyorModel[]>) => {
                if (response.success) {
                    dispatch(setSurveyorSearchResults(response.data));
                    dispatch(setSearchResultTotalRecord(response.totalRecord));
                } else {
                    Utility.Alert.Error(response.message);
                }
            }).catch((error) => {
                Utility.Alert.Error(error);
            })
            .finally(() => {
                dispatch(showLoading(false));
            });
    }, [SurveyorSearchData.triggerSearch])

    const getTonnageSearchClass = () => {
        let className = "nice-scroll ";
        if (savedSearchData.expanded) {
            className += "tonnage-search-wrapper";
        } else {
            className += "tonnage-search-wrapper-saved-search-hide";
        }

        return className;
    }

    return (
        <>
            <SavedSearch
                component={ComponentType.Surveyor}
            >
            </SavedSearch>
            <CustomAccordion
                title="Search"
                defaultOpen={true}
                contentClassName={getTonnageSearchClass()}>
                <div className='tonnage-search-body'>
                    <Grid container>
                        <Grid item xs={8}>
                            <Grid container>
                                <Grid xs={8}>
                                    <Tooltip title="Alt + W">
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            className="tonnage-search-save-button"
                                            onClick={() => {
                                                dispatch(setTriggerSurveyorSearch((new Date()).getTime()));
                                            }}
                                        >
                                            RUN SEARCH (w)
                                        </Button>
                                    </Tooltip>
                                </Grid>
                                <Grid xs={4}>
                                    <Tooltip title="Reset (Alt + Q)">
                                        <Button
                                            className="clear-input-button"
                                            color="success"
                                            variant="contained"
                                            onClick={() => {
                                                dispatch(resetSurveyorSearch());
                                                dispatch(setTriggerFocusCompanyName(new Date().getTime()));
                                            }}
                                        >
                                            <i className="fa fa-refresh" aria-hidden="true"></i>
                                        </Button>
                                    </Tooltip>
                                </Grid>
                            </Grid>

                        </Grid>
                        <Grid item xs={4}>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => {
                                    saveSavedSearch();
                                }}
                            >
                                Save Search
                            </Button>
                        </Grid>
                    </Grid>
                    <TagsFilter
                        data={_.map(SurveyorSearchData.selectedSurveyors, (item) => {
                            return {
                                id: item.id,
                                value: item.name,
                                positive: true
                            } as QueryItem
                        })}
                        useFreeText={true}
                        label="Surveyor Name"
                        onInputChange={(text: string) => {
                            dispatch(setSurveyorSearchKeyword(text));
                        }}
                        className="tonnage-search-item"
                        value={SurveyorSearchData.query.surveyorNames}
                        onValueChange={(value: QueryItem[]) => {
                            dispatch(setSurveyorNames(value));
                        }}
                        triggerFocusInput={SurveyorSearchData.triggerFocusCompanyName}
                    ></TagsFilter>
                    <TagsFilter
                        data={_.map(contactInputViewData.staticResources.countries, (item) => {
                            return {
                                id: item.code,
                                value: item.code,
                                positive: true
                            } as QueryItem
                        })}
                        label="Country"
                        className="tonnage-search-item"
                        value={SurveyorSearchData.query.countries}
                        onValueChange={(value: QueryItem[]) => {
                            dispatch(setCountries(value));
                        }}
                    ></TagsFilter>
                    <PortMultipleSelector
                        onPortChange={(value: PortModel[]) => {
                            if (value) {
                                dispatch(setPortNames(portsToQueryItems(value)));
                            }
                        }}
                        useFreeText={true}
                        portLabel='Port Open'
                        hideArea={true}
                        portValues={queryItemsToPorts(SurveyorSearchData.query.portNames)}
                        className="tonnage-search-item"
                        useAreaBookmark={true}
                    >
                    </PortMultipleSelector>
                </div>
            </CustomAccordion>
        </>
    )
};

export default SurveyorSearch;